// packages/shared-components/src/components/Loading/Loading.tsx

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";
import { useMessage } from "../../hooks/useMessage";
import { LoadingProps } from "./types";
import "./Loading.css";

const DEFAULT_DURATION = 2800;
const DEFAULT_COMPLETE_DELAY = 50;
const DEFAULT_MAX_DURATION = 50000;
const PROGRESS_MAX = 99;

/**
 * Portal 挂载点：子应用走 getSubAppPortalContainer（样式隔离）；主应用走 document.body。
 * 主应用若挂子应用 #root，qiankun 卸载/刷新时会先清 DOM，触发 Portal removeChild 报错。
 */
const resolveLoadingPortalContainer = (): HTMLElement => {
  if (
    typeof window !== "undefined" &&
    (window as Window & { __POWERED_BY_QIANKUN__?: boolean })
      .__POWERED_BY_QIANKUN__
  ) {
    return getSubAppPortalContainer();
  }
  return document.body;
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);

/**
 * 更拟真的自动进度：
 * - 前段快（快速反馈）
 * - 中段放缓（等待数据/设备）
 * - 后段趋近（接近完成但不直接到 100）
 * 全程单调递增（由调用方再用 max(prev, next) 兜底）。
 */
function computeNaturalProgress(elapsed: number, duration: number): number {
  const safeDuration = Math.max(600, duration);
  const ratio = elapsed / safeDuration;
  const t = clamp01(ratio);

  // 主体分段：0~55（快） -> 55~82（中） -> 82~94（慢）
  let base = 0;
  if (t < 0.25) {
    const p = easeOutCubic(t / 0.25);
    base = 55 * p;
  } else if (t < 0.7) {
    const p = easeOutQuad((t - 0.25) / 0.45);
    base = 55 + 27 * p;
  } else {
    const p = easeOutQuad((t - 0.7) / 0.3);
    base = 82 + 12 * p;
  }

  const tailElapsed = Math.max(0, elapsed - safeDuration);
  const tail = (PROGRESS_MAX - 94) * (1 - Math.exp(-tailElapsed / 3500));
  const withTail = Math.min(PROGRESS_MAX, base + tail);

  const rhythm = Math.sin(elapsed / 180) * 0.35 + Math.sin(elapsed / 520) * 0.2;
  return Math.min(PROGRESS_MAX, Math.max(0, withTail + rhythm));
}

export const Loading: React.FC<LoadingProps> = ({
  visible,
  text,
  className,
  progress: progressProp,
  autoProgress = false,
  autoProgressOptions,
  hidePercent = false,
  portalContainer: portalContainerProp,
}) => {
  const { t } = useTranslation();
  const message = useMessage();
  const duration = autoProgressOptions?.duration ?? DEFAULT_DURATION;
  const completeDelay =
    autoProgressOptions?.completeDelay ?? DEFAULT_COMPLETE_DELAY;
  const maxDuration = autoProgressOptions?.maxDuration ?? DEFAULT_MAX_DURATION;
  const onTimeout = autoProgressOptions?.onTimeout;
  const timeoutRedirectUrl = autoProgressOptions?.timeoutRedirectUrl;

  const [internalProgress, setInternalProgress] = useState(0);
  const [displayVisible, setDisplayVisible] = useState(visible);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const prevVisibleRef = useRef(visible);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const useAutoProgress = autoProgress;

  // 自动模拟进度 + 完成时延迟关闭 + 超时立即退出（禁止进度回退）
  // 仅当 visible 从 false→true 时重置，避免 effect 重跑导致进度回退
  useEffect(() => {
    if (!useAutoProgress) {
      if (visible) setDisplayVisible(true);
      else setDisplayVisible(false);
      prevVisibleRef.current = visible;
      return;
    }
    if (visible) {
      setDisplayVisible(true);
      const isNewSession = !prevVisibleRef.current;
      prevVisibleRef.current = true;
      if (isNewSession) {
        setInternalProgress(0);
        startTimeRef.current = Date.now();
      } else {
        // 非新会话（effect 重跑）：按当前进度反推 startTime，保证进度不重置、不跳变
        const p = Math.min(internalProgress, PROGRESS_MAX);
        startTimeRef.current = Date.now() - (p / PROGRESS_MAX) * duration;
      }
      let lastP = isNewSession ? 0 : internalProgress;

      const tick = () => {
        const elapsed = Date.now() - startTimeRef.current;
        if (elapsed >= maxDuration) {
          cancelAnimationFrame(rafRef.current);
          setDisplayVisible(false);
          message.error(t("common.loadFailed"));
          onTimeoutRef.current?.();
          // 触发最大时长视为数据发送失败，回到首页（与恢复出厂设置一致：延迟后 window.location，子应用内跳转主应用根路径更可靠）
          if (timeoutRedirectUrl !== false) {
            const href =
              typeof timeoutRedirectUrl === "string"
                ? timeoutRedirectUrl
                : `${window.location.origin}/`;
            setTimeout(() => {
              window.location.href = href;
            }, 300);
          }
          return;
        }
        const p = computeNaturalProgress(elapsed, duration);
        if (Math.floor(p) > Math.floor(lastP) || p >= PROGRESS_MAX) {
          setInternalProgress((prev) => Math.max(prev, p));
          lastP = p;
        }
        // 始终继续 tick，以便在达到 maxDuration 时能触发超时（进度到 92% 后不再更新进度，但需持续检测超时）
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafRef.current);
    } else {
      prevVisibleRef.current = false;
      cancelAnimationFrame(rafRef.current);
      setInternalProgress((prev) => Math.max(prev, 100));
      const tm = setTimeout(() => setDisplayVisible(false), completeDelay);
      return () => clearTimeout(tm);
    }
  }, [
    visible,
    useAutoProgress,
    duration,
    completeDelay,
    maxDuration,
    timeoutRedirectUrl,
  ]);

  const shouldHide = useAutoProgress ? !displayVisible : !visible;
  if (shouldHide) return null;

  const manualOrAutoProgress = useAutoProgress
    ? internalProgress
    : Number(progressProp);
  // Number(undefined) 等为 NaN；NaN ?? 0 仍为 NaN，需显式兜底，否则会显示「NaN%」
  const clampedProgress = Math.min(
    100,
    Math.max(
      0,
      Number.isFinite(manualOrAutoProgress) ? manualOrAutoProgress : 0
    )
  );

  const portalTarget =
    portalContainerProp != null
      ? portalContainerProp
      : resolveLoadingPortalContainer();

  return createPortal(
    <div className={`shared-loading-overlay ${className || ""}`}>
      <div className="shared-loading-content">
        <p className="shared-loading-text">{text || t("common.loading")}</p>
        {!hidePercent ? (
          <>
            <div className="shared-loading-progress-track">
              <div
                className="shared-loading-progress-bar"
                style={{ width: `${clampedProgress}%` }}
              />
            </div>
            <p className="shared-loading-percent">
              {Math.round(clampedProgress)}%
            </p>
          </>
        ) : null}
      </div>
    </div>,
    portalTarget
  );
};
